import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ArrowRight, Info } from 'lucide-react';
import { getEducationLabel } from '../lib/constants';

// Maps the field key the parser returns to a human-readable label and a formatter for display.
// Keep field keys in sync with what PUT /api/profile accepts (server.py update_worker_profile).
const FIELD_DISPLAY = {
  name:               { label: 'Full name' },
  phone:              { label: 'Phone' },
  town:               { label: 'Town' },
  tagline:            { label: 'Tagline' },
  years_of_experience:{ label: 'Years of experience' },
  // Show the friendly label ("Post Graduate", etc.) rather than the raw enum value —
  // and never surface the retired 'doctorate' value to the user.
  education_level:    { label: 'Education level', format: (v) => getEducationLabel(v) || String(v) },
  education_field:    { label: 'Field of study' },
  skills:             { label: 'Skills', format: (v) => Array.isArray(v) ? v.join(', ') : String(v) },
  work_history:       { label: 'Work history', format: (v) => Array.isArray(v) ? `${v.length} entries` : String(v) },
};

// Fields the parser may extract but PUT /api/profile does NOT accept.
// Show them as read-only info so the user knows we read them but can't update.
const READ_ONLY_FIELDS = new Set(['email']);

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

function isEmpty(v) {
  if (v === null || v === undefined || v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

function formatValue(key, value) {
  if (isEmpty(value)) return null;
  const fmt = FIELD_DISPLAY[key]?.format;
  return fmt ? fmt(value) : String(value);
}

/**
 * Modal shown after a resume parse. Displays a diff between current profile
 * fields and parsed values, with per-field apply/skip checkboxes. User clicks
 * "Apply" to commit selected updates; otherwise nothing changes.
 *
 * Default check state per field:
 *   - Currently empty + new value present  → checked (safe to fill)
 *   - Currently has data + new value differs → unchecked (don't overwrite without consent)
 *   - Values equal                          → not shown at all (nothing to do)
 */
export function ParseConfirmDialog({ open, parsed, currentProfile, onApply, onClose }) {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!parsed) return [];
    const out = [];
    for (const key of Object.keys(FIELD_DISPLAY)) {
      const newVal = parsed[key];
      if (isEmpty(newVal)) continue;
      const currentVal = currentProfile?.[key];
      if (valuesEqual(currentVal, newVal)) continue;
      out.push({
        key,
        label: FIELD_DISPLAY[key].label,
        currentDisplay: formatValue(key, currentVal),
        newDisplay: formatValue(key, newVal),
        wouldOverwrite: !isEmpty(currentVal),
      });
    }
    return out;
  }, [parsed, currentProfile]);

  // Read-only fields the parser found (e.g. email) — informational only
  const readOnlyFound = useMemo(() => {
    if (!parsed) return [];
    const out = [];
    for (const key of READ_ONLY_FIELDS) {
      if (!isEmpty(parsed[key])) {
        out.push({ key, label: key.charAt(0).toUpperCase() + key.slice(1), value: parsed[key] });
      }
    }
    return out;
  }, [parsed]);

  // Default selection: check fields that are currently empty (safe fill); leave overwrites unchecked
  const [selected, setSelected] = useState(() => {
    const initial = {};
    rows.forEach((r) => { initial[r.key] = !r.wouldOverwrite; });
    return initial;
  });

  // Re-init when rows change (e.g. dialog opens for a new parse)
  React.useEffect(() => {
    const initial = {};
    rows.forEach((r) => { initial[r.key] = !r.wouldOverwrite; });
    setSelected(initial);
  }, [rows]);

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));

  const handleApply = () => {
    const updates = {};
    for (const r of rows) {
      if (selected[r.key]) {
        updates[r.key] = parsed[r.key];
      }
    }
    onApply(updates);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('resume.confirm.title', 'Review changes from your resume')}</DialogTitle>
        </DialogHeader>

        {rows.length === 0 && readOnlyFound.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground">
            {t('resume.confirm.nothingNew', "Your resume didn't have any new info to add to your profile.")}
          </div>
        ) : (
          <>
            {rows.length > 0 && (
              <p className="text-sm text-muted-foreground mb-3">
                {t('resume.confirm.intro', "We read these fields from your resume. Check the ones you want to apply, then click Apply. Fields where your profile already has data are unchecked by default — tick them to overwrite.")}
              </p>
            )}

            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.key} className="flex items-start gap-3 border rounded-md p-3 bg-card">
                  <Checkbox
                    checked={!!selected[r.key]}
                    onCheckedChange={() => toggle(r.key)}
                    className="mt-1"
                    id={`pcd-${r.key}`}
                  />
                  <label htmlFor={`pcd-${r.key}`} className="flex-1 cursor-pointer">
                    <div className="font-medium text-sm">{r.label}</div>
                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center text-sm">
                      <div className="text-muted-foreground">
                        <span className="text-xs uppercase tracking-wider mr-1">Now:</span>
                        {r.currentDisplay || <span className="italic">(empty)</span>}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
                      <div className="text-foreground">
                        <span className="text-xs uppercase tracking-wider mr-1">From resume:</span>
                        {r.newDisplay}
                      </div>
                    </div>
                    {r.wouldOverwrite && (
                      <div className="text-xs text-amber-600 mt-1">
                        {t('resume.confirm.willOverwrite', 'This will replace your current value.')}
                      </div>
                    )}
                  </label>
                </div>
              ))}
            </div>

            {readOnlyFound.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium mb-1">
                      {t('resume.confirm.readOnlyTitle', 'Read-only — cannot be changed via auto-fill')}
                    </div>
                    <ul className="list-disc pl-5">
                      {readOnlyFound.map((f) => (
                        <li key={f.key}>
                          <span className="capitalize">{f.label}:</span> <span className="font-mono">{String(f.value)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs">
                      {t('resume.confirm.readOnlyHint', 'Email is tied to your sign-in and can only be changed via account settings.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleApply} disabled={selectedCount === 0}>
            {selectedCount === 0
              ? t('resume.confirm.applyZero', 'Apply')
              : t('resume.confirm.apply', `Apply ${selectedCount} update${selectedCount > 1 ? 's' : ''}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ParseConfirmDialog;
